/**
 * DOM helpers — the "no framework" primitive shared by the main app
 * and the library app.
 *
 * Components are plain functions that return HTMLElements. This module gives
 * them a tiny toolkit:
 *
 *   h('div', { class: 'x' }, child, child, ...)      → HTMLDivElement
 *   text('Hello')                                    → Text node
 *   empty(node)                                      → remove all children
 *   on(node, event, handler)                         → bind + return unbinder
 *   classes({ a: true, b: false })                   → 'a'
 *
 * No JSX, no tagged templates. Explicit but terse.
 */

export function h(tag, props, ...children) {
  const el = document.createElement(tag);
  if (props && typeof props === 'object' && !Array.isArray(props) && !(props instanceof Node)) {
    for (const [key, value] of Object.entries(props)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === 'class' || key === 'className') {
        el.className = typeof value === 'string' ? value : classes(value);
      } else if (key === 'style' && typeof value === 'object') {
        Object.assign(el.style, value);
      } else if (key === 'dataset' && typeof value === 'object') {
        Object.assign(el.dataset, value);
      } else if (key.startsWith('on') && typeof value === 'function') {
        // onclick → 'click'
        el.addEventListener(key.slice(2).toLowerCase(), value);
      } else if (key === 'html') {
        el.innerHTML = value; // trusted caller only — never for user input
      } else if (key === 'ref' && typeof value === 'function') {
        value(el);
      } else if (value === true) {
        el.setAttribute(key, '');
      } else {
        el.setAttribute(key, String(value));
      }
    }
  } else if (props !== undefined && props !== null) {
    // Called as h('div', child1, child2) — no props object.
    children.unshift(props);
  }

  for (const child of children.flat(Infinity)) {
    if (child === null || child === undefined || child === false) continue;
    if (child instanceof Node) el.appendChild(child);
    else el.appendChild(document.createTextNode(String(child)));
  }
  return el;
}

export function text(value) {
  return document.createTextNode(value ?? '');
}

export function empty(node) {
  if (!node) return;
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function on(target, event, handler, options) {
  target.addEventListener(event, handler, options);
  return () => target.removeEventListener(event, handler, options);
}

export function classes(map) {
  if (!map) return '';
  if (typeof map === 'string') return map;
  if (Array.isArray(map)) return map.filter(Boolean).join(' ');
  const out = [];
  for (const [k, v] of Object.entries(map)) if (v) out.push(k);
  return out.join(' ');
}
